import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import { LanguageProvider } from "./context/LanguageContext";

// Components
import HeroSection from "./components/HeroSection";
import FeaturesSection from "./components/FeaturesSection";
import PlansSection from "./components/PlansSection";
import BrokerSection from "./components/BrokerSection";
import AIJathagamSection from "./components/AIJathagamSection";
import Footer from "./components/Footer";
import MaintenancePage from "./components/common/Maintenance"; 
// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import MembershipPage from "./pages/MembershipPage";
import BrokerRegister from "./pages/BrokerRegister";
import BrokerInfo from "./pages/BrokerInfo";
import HelpPage from "./pages/HelpPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import UserManagement from "./pages/admin/UserManagement";
import ProfileModeration from "./pages/admin/ProfileModeration";
import MatchesManagement from "./pages/admin/MatchesManagement";
import ReportsManagement from "./pages/admin/ReportsManagement";
import RevenueManagement from "./pages/admin/RevenueManagement";
import MarketingManagement from "./pages/admin/MarketingManagement";
import AnalyticsReports from "./pages/admin/AnalyticsReports";
import MasterDataManagement from "./pages/admin/MasterDataManagement";
import AdminSettings from "./pages/admin/AdminSettings";

// Footer Pages
import AboutUsSection from "./components/footerpages/AboutUsSection";
import SuccessStoriesPage from "./components/footerpages/SuccessStoriesPage";
import BlogPage from "./components/footerpages/BlogPage";
import LiveSupportPage from "./components/footerpages/LiveSupportPage";
import FeedbackPage from "./components/footerpages/FeedbackPage";
import FAQsPage from "./components/footerpages/FAQsPage";
import SafetyTipsPage from "./components/footerpages/SafetyTipsPage";
import ReportMisusePage from "./components/footerpages/ReportMisusePage";
import ContactUs from "./components/footerpages/ContactUs";
import TermsAndConditions from "./components/footerpages/TermsAndConditions";
import CookiePolicy from "./components/footerpages/CookiePolicy";
import RefundPolicy from "./components/footerpages/RefundPolicy";

// Protected Routes
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/admin/AdminProtectedRoute";

// Common
import NotFound from "./components/common/NotFoundPage";
import BlockedUsers from "./pages/admin/Blockedusers";

// Maintenance Checker - This will handle global maintenance mode
import MaintenanceChecker from "./components/common/MaintenanceChecker";
const LandingPage = () => {
  return (
    <>
      <HeroSection />
      <FeaturesSection />
      <PlansSection />
      <BrokerSection />
      <AIJathagamSection />
      <Footer />
    </>
  );
};

const App = () => {
  React.useEffect(() => {
    // Request Notification Permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    // Request Location Permission (silently)
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(() => {}, () => {});
    }
  }, []);

  return (
    <LanguageProvider>
      <Router>
        {/* Wrap everything in MaintenanceChecker */}
        <MaintenanceChecker>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/membership" element={<MembershipPage />} />
            <Route path="/broker-register" element={<BrokerRegister />} />
            <Route path="/broker-info" element={<BrokerInfo />} />

            {/* Policy & Info Pages */}
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookie" element={<CookiePolicy />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/about-us" element={<AboutUsSection />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/success-stories" element={<SuccessStoriesPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/live-support" element={<LiveSupportPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/faqs" element={<FAQsPage />} />
            <Route path="/safety" element={<SafetyTipsPage />} />
            <Route path="/report" element={<ReportMisusePage />} />

            {/* Explicit Maintenance Route - Accessible even in maintenance mode */}
            <Route path="/maintenance" element={<MaintenancePage />} />

            {/* User Protected Route */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Admin Login */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Admin Protected Routes - Admins can access even during maintenance */}
            <Route path="/admin" element={<AdminProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="profiles" element={<ProfileModeration />} />
                <Route path="matches" element={<MatchesManagement />} />
                <Route path="blocked-users" element={<BlockedUsers />} />
                <Route path="reports" element={<ReportsManagement />} />
                <Route path="revenue" element={<RevenueManagement />} />
                <Route path="marketing" element={<MarketingManagement />} />
                <Route path="analytics" element={<AnalyticsReports />} />
                <Route path="master-data" element={<MasterDataManagement />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Route>

            {/* 404 - Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </MaintenanceChecker>
      </Router>
    </LanguageProvider>
  );
};

export default App;